<template>
  <mapit-dialog :dialog="isDialogOpen" @close="handleClose()" class="create-lead-dialog" customId="create-lead-dialog">
    <template v-slot:header-text>
      <h3 class="create-lead-dialog__title">{{ $t('title.create-lead') }}</h3>
    </template>
    <template v-slot:body v-if="isDialogOpen">
      <div>
        <m-form-row :label="$t('leads.form.lbl-telephone')">
          <contact-search-bar
            :no-client-subtitle="$t('contact.search-bar.no-client-subtitle-offer')"
            :client-history-text-button="$t('contact.search-bar.see-client-history')"
            :action-found-button-text="$t('buttons.create-client-offer')"
            :is-offer="false"
            :initial-prefix="extractedPrefix"
            :initial-phone-number="extractedNumber"
            :is-readonly="isContactSearchBarDisabled"
            :is-searchable="(!extractedPrefix || !extractedNumber) && !isContactSearchBarDisabled"
            :is-creating-lead="true"
            :no-hover="true"
            origin="leads"
            @get-dealer-contact-data="handleGetDealerContactData"
            @update:is-valid-phone="handleUpdateIsValidPhone"
            @update:phone-number="handleUpdatePhoneNumber" />
        </m-form-row>
      </div>
      <div>
        <m-form-row :label="$t('leads.form.lbl-firstName')">
          <m-text-field
            v-model="firstName"
            :placeholder="$t('leads.form.lbl-firstName-placeholder')"
            maxlength="50"
            data-cy="firstName"
            :required="firstNameIsRequired"
            :readonly="initialFirstName.length > 0"
            :disabled="arePersonalFieldsDisabled" />
        </m-form-row>
      </div>
      <div>
        <m-form-row :label="$t('leads.form.lbl-lastName')">
          <m-text-field
            v-model="lastName"
            :placeholder="$t('leads.form.lbl-lastName-placeholder')"
            maxlength="50"
            data-cy="lastName"
            :required="lastNameIsRequired"
            :readonly="initialLastName.length > 0"
            :disabled="arePersonalFieldsDisabled" />
        </m-form-row>
      </div>
      <div>
        <m-form-row :label="$t('leads.form.lbl-email')">
          <m-email-field
            v-model="email"
            :error-text="$t('leads.form.invalid-email')"
            :required="emailIsRequired"
            :readonly="initialEmail.length > 0"
            :disabled="arePersonalFieldsDisabled"
            @raw-input="handleRawEmailInput"
            @input="handleValidEmailInput" />
        </m-form-row>
      </div>
      <hr />
      <div>
        <m-form-row :label="$t('leads.form.lbl-channel')">
          <div class="m-input">
            <v-select
              v-model="channel"
              class="append-icon-mapit"
              clear-icon="z"
              append-icon="fa-light fa-angle-down"
              outlined
              :placeholder="$t('leads.form.lbl-channel-placeholder')"
              data-cy="channel"
              :items="['Web', 'Email', 'Whatsapp', 'Telefónico', 'Presencial']"
              :hide-details="true"
              :required="channelIsRequired"
              :disabled="areLeadFieldsDisabled" />
          </div>
        </m-form-row>
      </div>
      <div>
        <m-form-row :label="`${$t('leads.form.lbl-note')} (${$t('common.optional')})`">
          <m-text-field
            v-model="note"
            :placeholder="$t('leads.form.lbl-note-placeholder')"
            maxlength="50"
            data-cy="note"
            :required="noteIsRequired"
            :disabled="areLeadFieldsDisabled" />
        </m-form-row>
      </div>
      <hr />
      <v-row class="px-2 pt-2" no-gutters>
        <v-col cols="6" class="pr-2">
          <v-btn class="btn-gray" data-cy="btn-cancel" tabindex="-1" block @click="handleClose()">
            {{ $t('buttons.cancel') }}
          </v-btn>
        </v-col>
        <v-col cols="6" class="pl-2">
          <v-btn
            class="btn-primary"
            data-cy="btn-create"
            tabindex="-1"
            block
            @click="createLead"
            :loading="isLoading"
            :disabled="!areRequiredFieldsFilled">
            {{ $t('buttons.create') }}
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </mapit-dialog>
</template>
<script lang="ts">
import {computed, defineComponent, ref, watch} from '@vue/composition-api'
import {parsePhoneNumber} from 'libphonenumber-js'
import moment from 'moment'

import ridoApi from '@/api/rido.api'
import ContactSearchBar from '@/components/contact/ContactSearchBar.vue'
import MapitDialog from '@/components/dialog/MapitDialog.vue'
import {eventBus} from '@/main'
import {ContactType, LeadRequest} from '@/types'
export default defineComponent({
  name: 'CreateLeadDialog',
  props: {
    initialFirstName: {
      type: String,
      required: false,
      default: '',
    },
    initialLastName: {
      type: String,
      required: false,
      default: '',
    },
    initialEmail: {
      type: String,
      required: false,
      default: '',
    },
    isDialogOpen: {
      type: Boolean,
      required: true,
    },
    initialPhoneNumber: {
      type: String,
      required: false,
      default: '',
    },
    addressCountry: {
      type: String,
      required: false,
      default: '',
    },
    addressRegion: {
      type: String,
      required: false,
      default: '',
    },
    isInitiallyContactSearchBarDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isContactSearchBarSelectMode: {
      type: Boolean,
      required: false,
      default: true,
    },

    isCreatingFromClientHistory: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialPhoneIsSearched: {
      type: Boolean,
      required: false,
      default: false,
    },

    initialIsCreating: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {MapitDialog, ContactSearchBar},
  setup(props, {root, emit}) {
    const firstNameIsRequired = true
    const lastNameIsRequired = true
    const emailIsRequired = true
    const phoneIsRequired = true
    const channelIsRequired = true
    const noteIsRequired = false
    const isLoading = ref(false)
    const isCreating = ref(props.initialIsCreating)
    const isPhoneValid = ref(false)
    const isEmailValid = ref(false)
    const firstName = ref(props.initialFirstName)
    const lastName = ref(props.initialLastName)
    const email = ref(props.initialEmail)
    const isContactSearchBarDisabled = ref(props.isInitiallyContactSearchBarDisabled)
    const channel = ref('')
    const note = ref('')
    const phoneIsSearched = ref(props.initialPhoneIsSearched)
    const phone = ref(props.initialPhoneNumber)
    const extractedPrefix = ref('')
    const extractedNumber = ref('')

    const arePersonalFieldsDisabled = computed(() => {
      if (!phoneIsSearched.value) {
        return true
      }

      if (isCreating.value) {
        return false
      }

      return true
    })
    const areLeadFieldsDisabled = computed(() => {
      if (props.isCreatingFromClientHistory) {
        return false
      }

      if (!phoneIsSearched.value) {
        return true
      }

      return !firstName.value || !lastName.value || !email.value || !isEmailValid.value
    })
    const areRequiredFieldsFilled = computed(() => {
      return (
        firstName.value &&
        lastName.value &&
        email.value &&
        channel.value &&
        ((isEmailValid.value && isPhoneValid.value) ||
          props.isCreatingFromClientHistory ||
          (isEmailValid.value && isContactSearchBarDisabled.value))
      )
    })

    function extractPrefixAndNumber() {
      phone.value = props.initialPhoneNumber
      const phoneNumber = parsePhoneNumber(props.initialPhoneNumber)
      extractedPrefix.value = phoneNumber.countryCallingCode
      extractedNumber.value = phoneNumber.nationalNumber
    }

    function handleRawEmailInput(value: string) {
      email.value = value
      isEmailValid.value = false
    }

    function handleValidEmailInput(value: string) {
      email.value = value
      isEmailValid.value = true
    }

    watch(
      () => props.isDialogOpen,
      newValue => {
        if (newValue) {
          resetForm()
        }
      },
    )

    watch(
      () => props.initialPhoneNumber,
      newValue => {
        if (newValue) {
          extractPrefixAndNumber()
        }
      },
      {immediate: true},
    )

    function handleGetDealerContactData(dealerContact: any | null) {
      phoneIsSearched.value = true

      if (dealerContact === null) {
        isCreating.value = true
        firstName.value = ''
        lastName.value = ''
        email.value = ''
      } else {
        isContactSearchBarDisabled.value = true
        isCreating.value = false
        firstName.value = dealerContact.firstName || ''
        lastName.value = dealerContact.lastName || ''
        email.value = dealerContact.email || ''
      }
    }

    function resetForm() {
      isContactSearchBarDisabled.value = props.isInitiallyContactSearchBarDisabled
      phoneIsSearched.value = props.initialPhoneIsSearched
      isCreating.value = props.initialIsCreating
      firstName.value = props.initialFirstName
      lastName.value = props.initialLastName
      email.value = props.initialEmail
      channel.value = ''
      note.value = ''
    }

    function handleClose() {
      emit('onClose')
      resetForm()
      isContactSearchBarDisabled.value = props.isInitiallyContactSearchBarDisabled
    }

    async function createLead() {
      if (!firstName.value || !email.value) {
        return
      }

      isLoading.value = true
      try {
        const leadRequest: LeadRequest = {
          prospect: {
            type: ContactType.Individual,
            phone: phone.value,
            firstName: firstName.value,
            lastName: lastName.value,
            email: email.value,
          },
          opportunity: {
            // @ts-ignore
            type: 'MCInternalLead',
          },
          channel: {
            name: channel.value,
          },
          dealer: {
            code: root.$store.getters.getGroup?.code || '',
            id: root.$store.getters.getGroup?.dealerId ? `l-${root.$store.getters.getGroup?.dealerId?.slice(3)}` : '',
          },
          address: {
            country: props.addressCountry,
            addressRegion: props.addressRegion,
          },
        }

        if (note.value) {
          leadRequest.commentLog = [{text: note.value, timestamp: moment()}]
        }

        const res = await ridoApi.createLead(leadRequest)

        if (res.status === 201) {
          eventBus.$emit('on-created-lead', res.data)
          emit('onCreated', res.data)
        }
        emit('onClose')
        resetForm()
      } catch (error) {
        console.error('Error creating lead:', error)
      } finally {
        isLoading.value = false
      }
    }

    function handleUpdateIsValidPhone(isValid: boolean) {
      if (!isValid && !props.isCreatingFromClientHistory && !isContactSearchBarDisabled.value) {
        phone.value = ''
      }
      isPhoneValid.value = isValid
    }

    function handleUpdatePhoneNumber(newPhoneNumber: string) {
      if (!props.isCreatingFromClientHistory && !isContactSearchBarDisabled.value) {
        phone.value = newPhoneNumber
      }
    }

    return {
      firstNameIsRequired,
      lastNameIsRequired,
      emailIsRequired,
      phoneIsRequired,
      channelIsRequired,
      noteIsRequired,
      extractedPrefix,
      extractedNumber,
      isLoading,
      isCreating,
      firstName,
      lastName,
      email,
      channel,
      note,
      arePersonalFieldsDisabled,
      areLeadFieldsDisabled,
      areRequiredFieldsFilled,
      isContactSearchBarDisabled,
      isEmailValid,
      handleGetDealerContactData,
      handleUpdateIsValidPhone,
      handleUpdatePhoneNumber,
      handleClose,
      createLead,
      handleRawEmailInput,
      handleValidEmailInput,
    }
  },
  data() {
    return {
      phone: '',
    }
  },
})
</script>
<style lang="scss">
.create-lead-dialog {
  padding: 20px 32px !important;

  &__title {
    padding-bottom: 16px;
    border-bottom: 1px solid #c0c0c0;
  }
}

hr {
  border: none;
  border-top: 1px solid #c0c0c0;
  margin: 0.4rem;
}
</style>
